import {isFeatureFlagEnabled} from '@smart/platform-utils';
import type {PriceTypeV1} from '@smart/price-preferences-provider';
import type {OptionType} from '@smart/web-components';
import {RichText, Select, Text, TextButton} from '@smart/web-components';
import * as React from 'react';
import {useTransition} from 'react';
import type {
  FinanceOptions,
  FinanceParams,
  LeasingOptions,
  LeasingOptionsParams,
} from '../../bff/app-model/create-app-model';
import type {LeasingContent} from '../../bff/app-model/fetch-app-model';
import type {PriceDetailType} from '../../bff/price-details';
import {useAppModelContext} from '../hooks/use-app-model-context';
import type {DelayedStateResult} from '../hooks/use-delayed-state';
import {useLocale} from '../hooks/use-locale';
import type {MaybePriceDetails} from '../hooks/use-price-details';
import styles from './finance.module.scss';
import {LeasingTrademark} from './leasing-trademark';
import {LegalDisclaimers} from './legal-disclaimers';
import {PaymentSummary} from './payment-summary';

export interface FinanceProps {
  readonly content: LeasingContent;
  readonly options: LeasingOptions | FinanceOptions;
  readonly params: LeasingOptionsParams | FinanceParams;
  readonly onParamsChange: (key: string, value: any) => void;
  readonly priceDetailsResult: DelayedStateResult<MaybePriceDetails>;
  readonly priceType: PriceTypeV1;
  readonly paymentMode: LeasingContent['mode'];
  readonly onApply: () => void;
  readonly onErrorRetry: () => void;
}

export const Finance = ({
  content,
  options,
  params,
  onParamsChange,
  onApply,
  onErrorRetry,
  priceDetailsResult,
  priceType,
  paymentMode,
}: FinanceProps) => {
  const [_, startTransition] = useTransition();

  const {appModel, appMarketConfig} = useAppModelContext();
  const {featureFlagsConfig} = appModel;

  const {
    selects,
    applyButton,
    leasingTrademark,
    monthlyFeeLabel,
    monthlyFeeFetchError,
    paymentDetailsText,
    legalDisclaimer,
  } = content;

  const isLoading = priceDetailsResult.status !== `completed`;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onApply();
  };

  const getMonthFee = (priceDetails: PriceDetailType): string => {
    return priceDetails?.monthFee?.formattedValue ?? ``;
  };

  const handleSelectChange = React.useCallback(
    (key: string, value: string) =>
      startTransition(() => onParamsChange(key, value)),
    [params],
  );

  const locale = useLocale();

  const isHideSalesToolsLeasingRateEnabled = isFeatureFlagEnabled(
    `HIDE_SALES_TOOLS_LEASING_RATE`,
    locale.marketId,
    featureFlagsConfig,
  );

  const getOptions = React.useCallback(
    (name: string) => {
      const opts =
        (options[name as keyof typeof options] as OptionType[]) ?? [];
      if (name === `advancePayment`) {
        opts.sort((a, b) => parseInt(a.value) - parseInt(b.value));
      }
      return opts;
    },
    [options],
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <fieldset className={styles.fieldset}>
        {selects.map(({name, label}) => {
          return (
            <Select
              key={name}
              id={name}
              label={label}
              disabled={isLoading}
              options={getOptions(name)}
              name={name}
              value={String(
                params[name as keyof (LeasingOptionsParams | FinanceParams)],
              )}
              onChange={(e) => handleSelectChange(name, e.target.value)}
            />
          );
        })}
      </fieldset>
      {paymentDetailsText ? (
        <RichText
          mdString={paymentDetailsText}
          customOverrides={{
            p: {
              component: Text,
              props: {
                variant: `caption-200`,
                className: styles.disclaimer,
              },
            },
          }}
        />
      ) : null}
      {!isHideSalesToolsLeasingRateEnabled && (
        <div className={styles.divider} />
      )}
      <PaymentSummary
        label={monthlyFeeLabel}
        errorMessage={monthlyFeeFetchError}
        priceDetailsResult={priceDetailsResult}
        priceType={priceType}
        paymentMode={paymentMode}
        getPrice={getMonthFee}
        onErrorRetry={onErrorRetry}
      />
      <TextButton
        className={styles.applyButton}
        type="submit"
        variant="primary"
        size="300"
      >
        {applyButton.children}
      </TextButton>
      {appMarketConfig.showLeasingLogotype && leasingTrademark?.imagePath && (
        <LeasingTrademark
          imagePath={leasingTrademark.imagePath}
          externalLink={leasingTrademark?.externalLink}
        />
      )}

      {legalDisclaimer && legalDisclaimer.length > 0 && (
        <LegalDisclaimers legalDisclaimers={legalDisclaimer} />
      )}
    </form>
  );
};
